import React from 'react';

import { cn } from '@/utils';

/** Which KPI tile is "active" — drives the case grid's risk filter. */
export type KpiFilter = 'all' | 'warning' | 'breached';

interface KpiStripProps {
  open: number;
  atRisk: number;
  breached: number;
  online: number;
  totalAgents: number;
  /** Currently active filter — controlled by `WorkforceContainer`. */
  activeFilter: KpiFilter;
  onFilterChange: (next: KpiFilter) => void;
}

type Tone = 'default' | 'attention' | 'critical';

interface KpiCardProps {
  value: string | number;
  label: string;
  tone?: Tone;
  active: boolean;
  onClick: () => void;
}

const cardTone: Record<Tone, string> = {
  default: 'bg-muted/40',
  attention: 'bg-attention',
  critical: 'bg-critical',
};

const labelTone: Record<Tone, string> = {
  default: 'text-muted-foreground',
  attention: 'text-attention-foreground/80',
  critical: 'text-critical-foreground/80',
};

const valueTone: Record<Tone, string> = {
  default: 'text-foreground',
  attention: 'text-attention-foreground',
  critical: 'text-critical-foreground',
};

const ringTone: Record<Tone, string> = {
  default: 'ring-foreground/40',
  attention: 'ring-attention-foreground/60',
  critical: 'ring-critical-foreground/60',
};

function KpiCard({ value, label, tone = 'default', active, onClick }: KpiCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        // `min-w-[160px]` keeps the cards visually balanced when the number is
        // a single digit; they otherwise size to their content (no flex-grow).
        'min-w-[160px] rounded-lg px-4 py-3 text-left transition-all cursor-pointer',
        cardTone[tone],
        // Subtle hover lift; ring when active so it reads as "selected filter".
        'hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        active && `ring-2 ring-offset-1 ${ringTone[tone]}`,
      )}
    >
      <div className={cn('text-[11px] font-semibold uppercase tracking-wider', labelTone[tone])}>
        {label}
      </div>
      <div className={cn('mt-1 text-3xl font-bold leading-tight tabular-nums', valueTone[tone])}>
        {value}
      </div>
    </button>
  );
}

export function KpiStrip({
  open,
  atRisk,
  breached,
  online,
  totalAgents,
  activeFilter,
  onFilterChange,
}: KpiStripProps) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Cards size to their content — no `flex-1` / `grid-cols` so they
          don't stretch to fill the row. */}
      <KpiCard
        label="Open"
        value={open}
        active={activeFilter === 'all'}
        onClick={() => onFilterChange('all')}
      />
      <KpiCard
        label="At-Risk"
        value={atRisk}
        tone="attention"
        active={activeFilter === 'warning'}
        onClick={() => onFilterChange(activeFilter === 'warning' ? 'all' : 'warning')}
      />
      <KpiCard
        label="Breached"
        value={breached}
        tone="critical"
        active={activeFilter === 'breached'}
        onClick={() => onFilterChange(activeFilter === 'breached' ? 'all' : 'breached')}
      />
      {/* Compact members-online indicator on the right — green dot + count. */}
      <span className="ml-auto inline-flex items-center gap-2 text-sm text-muted-foreground tabular-nums whitespace-nowrap shrink-0">
        <span
          className={cn(
            'size-2 rounded-full',
            online > 0 ? 'bg-positive-foreground' : 'bg-muted-foreground/40',
          )}
          aria-hidden="true"
        />
        {online}/{totalAgents} members online
      </span>
    </div>
  );
}
